import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { resolveUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { CodeBlock } from "@/components/CodeBlock";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Clock } from "lucide-react";

function PostSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 space-y-5" data-testid="blog-detail-skeleton">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-12 w-full rounded-xl" />
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-72 w-full rounded-2xl" />
      {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-4 w-full" />)}
    </div>
  );
}

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blogs/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blog-not-found">
        <Seo title="Post not found" noIndex path={`/blog/${slug}`} />
        <h1 className="text-3xl font-heading">Post not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Back to blog</Link>
      </div>
    );

  if (!post) return <PostSkeleton />;

  return (
    <article className="max-w-3xl mx-auto px-5 sm:px-6 py-10" data-testid="blog-detail-page">
      <Seo
        title={post.title}
        description={post.meta_description || post.excerpt}
        image={resolveUrl(post.cover_image)}
        path={`/blog/${post.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.meta_description || post.excerpt,
          image: resolveUrl(post.cover_image),
          author: { "@type": "Person", name: post.author || "Sandeep Varikuppala" },
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          keywords: (post.tags || []).join(", "),
        }}
      />
      <Reveal>
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="w-4 h-4" /> Blog</Link>
        <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono">
          <span>{new Date(post.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
          {post.read_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.read_time}</span>}
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-light tracking-tight mt-3 leading-[1.08]">{post.title}</h1>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(post.tags || []).map((t) => (
            <span key={t} className="text-[11px] font-mono px-2 py-1 rounded-md bg-secondary text-secondary-foreground">{t}</span>
          ))}
        </div>
      </Reveal>

      {post.cover_image && (
        <Reveal>
          <img
            src={resolveUrl(post.cover_image)}
            alt={post.title}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            className="w-full rounded-3xl mt-8 glass p-2 max-h-[60vh] object-cover"
          />
        </Reveal>
      )}

      <Reveal>
        <div className="prose-portfolio mt-10">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ node, ...p }) => <h2 className="text-2xl font-heading tracking-tight mt-10 mb-4" {...p} />,
              h3: ({ node, ...p }) => <h3 className="text-xl font-heading tracking-tight mt-8 mb-3" {...p} />,
              p: ({ node, children, ...p }) => {
                const hasBlock = node?.children?.some(
                  (c) => c.type === "element" && ["pre", "img", "div", "blockquote", "ol", "ul"].includes(c.tagName)
                );
                if (hasBlock) return <div className="my-4">{children}</div>;
                return <p className="text-base sm:text-lg text-muted-foreground leading-relaxed my-4" {...p}>{children}</p>;
              },
              ul: ({ node, ...p }) => <ul className="list-disc pl-6 space-y-2 my-4 text-muted-foreground" {...p} />,
              ol: ({ node, ...p }) => <ol className="list-decimal pl-6 space-y-2 my-4 text-muted-foreground" {...p} />,
              li: ({ node, ...p }) => <li className="text-base sm:text-lg leading-relaxed" {...p} />,
              a: ({ node, ...p }) => <a className="underline underline-offset-2 hover:text-foreground" target="_blank" rel="noreferrer" {...p} />,
              blockquote: ({ node, ...p }) => <blockquote className="border-l-2 border-border pl-4 italic text-muted-foreground my-6" {...p} />,
              strong: ({ node, ...p }) => <strong className="text-foreground font-semibold" {...p} />,
              img: ({ node, ...p }) => <img className="rounded-2xl my-6 w-full" alt="" {...p} />,
              code: ({ node, ...props }) => (
                <code className="font-mono text-sm px-1.5 py-0.5 rounded bg-secondary text-foreground break-words" {...props} />
              ),
              pre: ({ node, children }) => {
                const codeEl = node?.children?.[0];
                const text = codeEl?.children?.[0]?.value ?? "";
                const cls = (codeEl?.properties?.className || [])[0] || "";
                const lang = /language-(\w+)/.exec(cls)?.[1] || "text";
                return (
                  <div className="my-6">
                    <CodeBlock snippet={{ title: lang, language: lang, code: text.replace(/\n$/, "") }} />
                  </div>
                );
              },
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>
      </Reveal>

      <div className="mt-16 glass rounded-3xl p-8 text-center">
        <h3 className="font-heading text-xl">Enjoyed this?</h3>
        <p className="text-sm text-muted-foreground mt-2">Let's talk about your next project.</p>
        <Link to="/contact" className="mt-5 inline-flex rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity">Get in touch</Link>
      </div>
    </article>
  );
}
